import type React from 'react';
import userPersonas from '@common/constants/userPersonas';
import StringConstants from '@common/constants/StringConstants';
import Modal from '@rental-manager/pages/Onboarding/WizardShell/WizardScreen';
import {
  Anchor,
  Box,
  Button,
  FieldSet,
  FormHelp,
  Heading,
  Label,
  LabeledControl,
  Legend,
  List,
  Paragraph,
  Radio,
  Text,
  TextButton,
} from '@zillow/constellation-10';

type Props = {
  onSubmit: () => void;
  selectedOption: string;
  setSelectedOption: (selectedOption: string) => void;
  onClose: () => void;
  onBack: () => void;
  emergencyMode: boolean;
};

const TaxTypeSelectorPage: React.FC<React.PropsWithChildren<Props>> = (props: Props): JSX.Element => {
  return (
    <Modal
      persona={userPersonas.LANDLORD}
      onRequestClose={props.emergencyMode ? undefined : props.onClose}
      header="Payment setup"
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'default',
          }}
        >
          <Heading level={3} textStyle="body-lg-bold">
            Verify your tax information
          </Heading>
          <Paragraph>
            Verifying your tax status and identification numbers will help ensure that payments you collect are
            correctly reported.
          </Paragraph>
          <TextButton asChild css={{ justifySelf: 'start' }}>
            <a
              href={StringConstants.LINKS.TAX_VERIFICATION_LEARN_MORE.LANDLORD}
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more
            </a>
          </TextButton>
          <List.Root>
            <List.Item>This information won’t be shared with renters</List.Item>
            <List.Item>Zillow doesn’t store this information</List.Item>
          </List.Root>
          <FieldSet
            required
            legend={
              <Legend css={{ textStyle: 'body-bold' }}>
                For this rental’s income, are you taxed as an individual or a business?
              </Legend>
            }
            description={
              <FormHelp css={{ display: 'block' }}>
                By continuing, you agree to comply with our third-party payment processor’s{' '}
                <Anchor target="_blank" rel="noopener noreferrer" href="https://stripe.com/us/connect-account/legal">
                  Payment Terms
                </Anchor>{' '}
                and{' '}
                <Anchor target="_blank" rel="noopener noreferrer" href="https://plaid.com/legal">
                  Bank Authorization Terms
                </Anchor>
              </FormHelp>
            }
          >
            <LabeledControl
              controlId="tax-type-individual"
              control={
                <Radio
                  name="tax-type"
                  onChange={() => props.setSelectedOption('individual')}
                  checked={props.selectedOption === 'individual'}
                  value="individual"
                />
              }
              label={
                <Label>
                  An individual
                  <br />
                  <Text css={{ color: 'text.subtle' }}>
                    You rent property that you own as an investment, and don’t separate the income from any other
                    personal income you have.
                  </Text>
                </Label>
              }
            />
            <LabeledControl
              controlId="tax-type-company"
              control={
                <Radio
                  name="tax-type"
                  onChange={() => props.setSelectedOption('company')}
                  checked={props.selectedOption === 'company'}
                  value="company"
                />
              }
              label={
                <Label>
                  A business
                  <br />
                  <Text css={{ color: 'text.subtle' }}>
                    Your rental property is owned by a separate business entity, and the rental income is kept separate
                    from your personal income.
                  </Text>
                </Label>
              }
            />
          </FieldSet>
        </Box>
      }
      backAction={props.emergencyMode ? undefined : { onClick: props.onBack }}
      footerAriaLabel="Tax type selection actions"
      footerEnd={
        <Button emphasis="filled" disabled={!props.selectedOption} onClick={props.onSubmit}>
          Continue
        </Button>
      }
    />
  );
};

export default TaxTypeSelectorPage;
